import {useMemo} from "react";

import {FetchPolicy, type Result} from "@khanacademy/wonder-blocks-data";
import {useQuery} from "@khan/data-gql";
import {Errors, KAError} from "@khan/logging";

import {USE_CONFIGURABLE_INPUTS} from "../queries/use-configurable-inputs-query.ts";

interface Option {
    description?: string;
    key: string;
    descriptionDoNotUse?: string;
}

export enum InputKind {
    singleSelect = "SINGLE_SELECT",
    textArea = "TEXT_AREA",
    textBox = "TEXT_BOX",
    richText = "RICH_TEXT",
}

enum InputType {
    number = "number",
    text = "text",
    password = "password",
    email = "email",
    tel = "tel",
}

export interface ValidationOptions {
    minimum?: number;
    maximum?: number;
    pattern?: string;
}

export type ConfigurableInput =
    | {
          key: string;
          title?: string;
          additionalNote?: string;
          description?: string;
          placeholderText?: string;
          inputKind: InputKind.singleSelect;
          options: Option[];
          optional?: boolean;
          validationOptions?: ValidationOptions;
      }
    | {
          title?: string;
          additionalNote?: string;
          description?: string;
          placeholderText?: string;
          key: string;
          inputKind: InputKind.textArea;
          optional?: boolean;
          validationOptions?: ValidationOptions;
      }
    | {
          title?: string;
          additionalNote?: string;
          description?: string;
          placeholderText?: string;
          key: string;
          inputKind: InputKind.textBox;
          inputType: InputType;
          optional?: boolean;
          validationOptions?: ValidationOptions;
      }
    | {
          title?: string;
          additionalNote?: string;
          description?: string;
          placeholderText?: string;
          key: string;
          inputKind: InputKind.richText;
          optional?: boolean;
          validationOptions?: ValidationOptions;
      };

export const useConfigurableInputsForActivity = (
    contentID: string,
): Result<Array<ConfigurableInput>> => {
    const [result] = useQuery(USE_CONFIGURABLE_INPUTS, {
        variables: {contentID},
        fetchPolicy: FetchPolicy.NetworkOnly,
        skip: !contentID,
    });

    const value = useMemo<Result<Array<ConfigurableInput>>>(() => {
        if (result.status === "success") {
            if (!result.data.aiGuideActivityById?.configurableInputs) {
                return {
                    status: "success",
                    data: [],
                };
            }

            try {
                const configurableInputs: Array<ConfigurableInput> = JSON.parse(
                    result.data.aiGuideActivityById.configurableInputs,
                );
                return {
                    status: "success",
                    data: configurableInputs,
                };
            } catch (err) {
                if (err instanceof Error) {
                    return {
                        status: "error",
                        error: err,
                    };
                }

                return {
                    status: "error",
                    error: new KAError(
                        `unknown error occured while parsing JSON for ${contentID}`,
                        Errors.Internal,
                    ),
                };
            }
        }

        return result;
    }, [result, contentID]);

    return value;
};
